/**
 * Reduz a foto no navegador antes de enviar.
 *
 * Foto de celular chega com 3–8 MB e 4000 px; o card do catálogo mostra
 * algumas centenas de pixels. Reduzir aqui mantém o repositório leve, o
 * envio rápido no 4G e o site carregando depressa — sem o cliente precisar
 * saber o que é WebP.
 */

const MAX_SIDE = 1000;
const QUALITY = 0.85;

export interface ResizedImage {
  /** Conteúdo em base64, sem o prefixo `data:`. */
  base64: string;
  extension: 'webp' | 'jpg';
  /** URL local para a prévia no painel. */
  previewUrl: string;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Não foi possível abrir esta imagem.'));
    };
    image.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, QUALITY));
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('Falha ao ler a imagem.'));
    reader.readAsDataURL(blob);
  });
}

export async function resizeImage(file: File): Promise<ResizedImage> {
  const image = await loadImage(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.round(image.naturalWidth * scale);
  const height = Math.round(image.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Seu navegador não conseguiu processar a imagem.');

  // Fundo branco: o JPEG (usado quando o navegador não gera WebP) não tem
  // transparência, e uma embalagem em PNG recortado ficaria com fundo preto.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  // Safari antigo devolve PNG quando não sabe gerar WebP; aí vai de JPEG.
  let blob = await toBlob(canvas, 'image/webp');
  let extension: ResizedImage['extension'] = 'webp';
  if (!blob || blob.type !== 'image/webp') {
    blob = await toBlob(canvas, 'image/jpeg');
    extension = 'jpg';
  }
  if (!blob) throw new Error('Seu navegador não conseguiu processar a imagem.');

  return { base64: await toBase64(blob), extension, previewUrl: URL.createObjectURL(blob) };
}
